/**
 * SidebarStep5 - BI Review + Causation Test (Historical BI Path)
 * 
 * This component shows the BI calculation results and causation test analysis.
 * Shows when calculation_mode === 'standard_bi' (default)
 * 
 * Features:
 *   - BI calculation results
 *   - Causation test (V-Test) results
 *   - Revenue/VP charts
 *   - Editable financial grid
 *   - K-1 partner summary
 * 
 * Preserves ALL functionality from Step5_BI_Review.tsx - only layout changes.
 */

import React, { useEffect, useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, LineChart, Line 
} from 'recharts';
import { 
  CheckCircle, XCircle, AlertCircle, TrendingDown, TrendingUp,
  DollarSign, Edit3, Eye, Users, Clock, Calculator, Loader2,
  FileText, AlertTriangle
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { useAuth } from '../../../contexts/TestAuthContext';
import EditableFinancialGrid from '../EditableFinancialGrid';
import K1PartnerSummary from '../K1PartnerSummary';
import PartialYearBanner from '../PartialYearBanner';

// Currency formatter
const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '$0.00';
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface SidebarStep5Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  claimId?: string;
}

interface BIResult {
  step1: {
    variable_profit_loss: number;
    benchmark_vp: number;
    coverage_vp: number;
  };
  step2: {
    incremental_loss: number;
    revenue_adjustment: number;
    variable_margin: number;
  };
  final_claim: number;
  causation: {
    test_passed: string;
    p_value: number;
    confidence_level: string;
    qualifying_periods: string[];
    variance: number;
    threshold: number;
    ci_lower: number;
    ci_upper: number;
  };
  event_type: string;
  event_display_name: string;
}

export default function SidebarStep5({ 
  onNext, 
  onBack, 
  goToStep,
  data, 
  onDataUpdate, 
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  claimId
}: SidebarStep5Props) {
  const [result, setResult] = useState<BIResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeView, setActiveView] = useState<'preview' | 'editor' | 'k1summary'>('preview');
  const [reportId, setReportId] = useState<string | null>(null);
  const [dataCompleteness, setDataCompleteness] = useState<{complete: boolean, missing: string[]}>({complete: false, missing: []});
  const [editedFinancialData, setEditedFinancialData] = useState<any>(null);
  const [partialYearInfo, setPartialYearInfo] = useState<any>(null);  // Track partial year handling
  const [warnings, setWarnings] = useState<string[]>([]);  // Track warnings
  const { getToken } = useAuth();

  // Validate data completeness
  const validateDataCompleteness = () => {
    const missing: string[] = [];
    const financialData = data.financial_data || {};
    
    const calculateRequiredYears = () => {
      if (data.calculated_years && Array.isArray(data.calculated_years) && data.calculated_years.length > 0) {
        return data.calculated_years;
      }
      
      if (financialData) {
        const dataYears = Object.keys(financialData)
          .filter(key => key.startsWith('data_'))
          .map(key => parseInt(key.replace('data_', '')))
          .filter(y => !isNaN(y))
          .sort((a, b) => a - b);
        
        if (dataYears.length > 0) return dataYears;
      }
      
      const currentYear = new Date().getFullYear();
      return [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
    };
    
    const requiredYears = calculateRequiredYears();
    const missingYears: number[] = [];
    const minRequiredYears = Math.min(3, requiredYears.length);
    let yearsWithData = 0;
    
    requiredYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = financialData[yearKey];
      
      if (!yearData || !Array.isArray(yearData) || yearData.length === 0) {
        missingYears.push(year);
      } else {
        const hasRealRevenue = yearData.some((month: any) => (month.revenue || 0) > 0);
        if (!hasRealRevenue) {
          missingYears.push(year);
        } else {
          yearsWithData++;
        }
      }
    });
    
    if (yearsWithData < minRequiredYears) {
      missing.push(`Need at least ${minRequiredYears} years of financial data (have ${yearsWithData})`);
    }
    
    return { complete: missing.length === 0, missing };
  };

  // Run BI calculation
  useEffect(() => {
    const runCalculation = async () => {
      try {
        setLoading(true);
        setError('');
        
        const completeness = validateDataCompleteness();
        setDataCompleteness(completeness);
        
        if (!completeness.complete) {
          setLoading(false);
          return;
        }
        
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const token = await getToken();
        
        const payload = {
          client_name: data.client_name,
          claimant_name: data.claimant_name,
          business_address: data.business_address,
          zone: data.zone,
          business_type: data.business_type,
          event_type: data.event_type,
          event_start_date: data.event_start_date,
          event_end_date: data.event_end_date,
          coverage_year: data.coverage_year,
          financial_data: data.financial_data,
          payroll_data: data.payroll_data || {},
          calculated_years: data.calculated_years || [],
          tax_data: data.tax_data || [],
        };

        // CRITICAL FIX: Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder
        // "Response.clone: Body has already been consumed" error
        const validationResult = await new Promise<any>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${BACKEND_URL}/api/v1/reports/validate`);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve(data);
              } catch (e) {
                reject(new Error('Failed to parse validation response'));
              }
            } else {
              reject(new Error('Failed to validate report'));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error during validation'));
          xhr.send(JSON.stringify(payload));
        });
        
        console.log('🔍 BI Validation Result:', validationResult);
        
        if (validationResult.report_id) {
          setReportId(validationResult.report_id);
        }
        
        // Capture partial year handling info and warnings
        if (validationResult.partial_year_handling) {
          console.log('📅 Partial year handling applied:', validationResult.partial_year_handling);
          setPartialYearInfo(validationResult.partial_year_handling);
        }
        if (validationResult.warnings && validationResult.warnings.length > 0) {
          console.log('⚠️ Warnings:', validationResult.warnings);
          setWarnings(validationResult.warnings);
        }
        
        // Backend returns result directly, not nested under bi_result
        if (validationResult.valid) {
          console.log('✅ Valid response - setting result');
          setResult(validationResult);
        } else if (validationResult.bi_result) {
          console.log('✅ Nested bi_result - setting result');
          setResult(validationResult.bi_result);
        } else {
          console.log('⚠️ Invalid response - no result set', validationResult);
          // Still set the result for debugging visibility
          if (validationResult.errors) {
            setError(validationResult.errors.join(', ') || 'Validation failed');
          }
        }
        
      } catch (err: any) {
        console.error('BI calculation error:', err);
        setError(err.message || 'Failed to calculate BI');
      } finally {
        setLoading(false);
      }
    };

    runCalculation();
  }, [data, getToken]);

  // Handle next
  const handleNext = () => {
    onNext({
      ...data,
      bi_result: result,
      report_id: reportId,
    });
  };

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered (Step 5):', action);
    switch (action) {
      case 'rerun':
        // Re-run the BI calculation
        setLoading(true);
        setResult(null);
        // The useEffect will trigger runCalculation when loading changes
        setTimeout(() => {
          window.location.reload(); // Simple way to re-run - triggers fresh calculation
        }, 100);
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  // Prepare chart data
  const chartData = (() => {
    const financialData = editedFinancialData || data.financial_data || {};
    const yearDataArray: { year: string; revenue: number; vp: number; label: string }[] = [];
    
    Object.keys(financialData).forEach(key => {
      const match = key.match(/^data_(\d{4})$/);
      if (match) {
        const year = match[1];
        const yearData = financialData[key];
        
        if (Array.isArray(yearData) && yearData.length > 0) {
          const totalRevenue = yearData.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
          const totalPayroll = yearData.reduce((sum: number, m: any) => sum + (m.payroll || 0), 0);
          const totalCOGS = yearData.reduce((sum: number, m: any) => sum + (m.cogs || 0), 0);
          const variableProfit = totalRevenue - totalCOGS - totalPayroll;
          
          if (totalRevenue > 0) {
            yearDataArray.push({
              year,
              revenue: totalRevenue,
              vp: variableProfit > 0 ? variableProfit : totalRevenue * 0.3,
              label: year
            });
          }
        }
      }
    });
    
    return yearDataArray.sort((a, b) => parseInt(a.year) - parseInt(b.year));
  })();

  // Calculate completion
  const completionPercent = 85;
  const causationPassed = result?.causation?.test_passed && result.causation.test_passed !== 'No Test Passed';
  const grade = causationPassed ? 'A' : result ? 'B' : 'C';

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'causation',
      label: 'Causation Test',
      isActive: activeView === 'preview',
      isComplete: !!result?.causation,
      onClick: () => setActiveView('preview')
    },
    {
      id: 'bi',
      label: 'BI Results',
      isActive: false,
      isComplete: !!result?.final_claim,
      onClick: () => setActiveView('preview')
    },
    {
      id: 'editor',
      label: 'Edit Data',
      isActive: activeView === 'editor',
      isComplete: false,
      onClick: () => setActiveView('editor')
    },
    {
      id: 'k1',
      label: 'K-1 Summary',
      isActive: activeView === 'k1summary',
      isComplete: (data.tax1065Files?.length || 0) > 0,
      onClick: () => setActiveView('k1summary')
    }
  ];

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    if (result) {
      items.push({ id: 'calc', label: 'BI calculated', status: 'ok' });
    }
    
    if (causationPassed) {
      items.push({ id: 'causation', label: 'Causation passed', status: 'ok' });
    } else if (result) {
      items.push({ id: 'causation', label: 'Causation failed', status: 'warning' });
    }
    
    return items;
  };

  // Loading state
  if (loading) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Calculating..."
        completionPercent={80}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="C"
        sections={[]}
        qaItems={[]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            <p className="text-lg font-medium text-gray-700">Running BI Calculation...</p>
            <p className="text-sm text-gray-500">Analyzing financial data and causation</p>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  // Data incomplete state
  if (!dataCompleteness.complete) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Missing required data"
        completionPercent={70}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="D"
        sections={[]}
        qaItems={[{ id: 'data', label: 'Data incomplete', status: 'missing' }]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Missing Required Data</h2>
            <p className="text-gray-600 mb-6">
              The following data is required for BI calculation:
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <ul className="space-y-2 text-left">
                {dataCompleteness.missing.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-amber-800">
                    <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => goToStep ? goToStep(3) : onBack()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              ← Go Back to Financial Data
            </button>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  // Error state
  if (error && !result) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Calculation error"
        completionPercent={75}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="D"
        sections={[]}
        qaItems={[{ id: 'error', label: 'Error occurred', status: 'warning' }]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Calculation Error</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={onBack}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={5}
      highestCompletedStep={highestCompletedStep}
      stepTitle="BI Review + Causation Test"
      stepSubtitle={result ? `Final Claim: ${formatCurrency(result.final_claim)}` : 'Review calculation results'}
      completionPercent={completionPercent}
      calculationMode={data.calculation_mode || 'standard_bi'}
      grade={grade}
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNext}
      claimId={claimId}
      nextLabel="Continue to Report"
      isNextDisabled={!result}
      showNext={true}
      onQuickAction={handleQuickAction}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* View Tabs */}
        <div className="flex gap-2 border-b border-gray-200 pb-4">
          <button
            onClick={() => setActiveView('preview')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === 'preview'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Preview
          </button>
          <button
            onClick={() => setActiveView('editor')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeView === 'editor'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Edit3 className="w-4 h-4 inline mr-2" />
            Edit Data
          </button>
          {(data.tax1065Files?.length || 0) > 0 && (
            <button
              onClick={() => setActiveView('k1summary')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeView === 'k1summary'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              K-1 Summary
            </button>
          )}
        </div>

        {/* Preview View */}
        {activeView === 'preview' && result && (
          <div className="space-y-6">
            {/* Enhanced Partial Year Banner with Timeline */}
            {partialYearInfo && (
              <PartialYearBanner partialYearInfo={partialYearInfo} />
            )}
            
            {/* Additional Warnings */}
            {warnings.length > 0 && !partialYearInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-900">Calculation Warnings</h4>
                    <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                      {warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Causation Test Result */}
            <div className={`rounded-xl p-6 border-2 ${
              causationPassed 
                ? 'bg-green-50 border-green-300' 
                : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center gap-4 mb-4">
                {causationPassed ? (
                  <CheckCircle className="w-12 h-12 text-green-600" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-600" />
                )}
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Causation Test: {causationPassed ? 'PASSED' : 'FAILED'}
                  </h3>
                  <p className={`text-sm ${causationPassed ? 'text-green-700' : 'text-red-700'}`}>
                    {result.causation.test_passed || 'No test passed'}
                    {result.causation.confidence_level && ` (${result.causation.confidence_level})`}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">P-Value</p>
                  <p className="text-lg font-bold text-gray-900">{(result.causation.p_value * 100).toFixed(1)}%</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Variance</p>
                  <p className="text-lg font-bold text-gray-900">{(result.causation.variance * 100).toFixed(1)}%</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Threshold</p>
                  <p className="text-lg font-bold text-gray-900">{(result.causation.threshold * 100).toFixed(0)}%</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Confidence</p>
                  <p className="text-lg font-bold text-gray-900">{result.causation.confidence_level}</p>
                </div>
              </div>
            </div>

            {/* Final Claim */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Final Claim Amount</p>
                  <p className="text-4xl font-bold mt-1">{formatCurrency(result.final_claim)}</p>
                </div>
                <DollarSign className="w-16 h-16 text-blue-200 opacity-50" />
              </div>
            </div>

            {/* BI Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-gray-500">Variable Profit Loss</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(result.step1.variable_profit_loss)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-blue-500" />
                  <p className="text-sm text-gray-500">Benchmark VP</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(result.step1.benchmark_vp)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <p className="text-sm text-gray-500">Coverage Period VP</p>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(result.step1.coverage_vp)}
                </p>
              </div>
            </div>

            {/* Revenue Chart */}
            {chartData.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Revenue by Year</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                    <Bar dataKey="vp" fill="#10b981" name="Variable Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Editor View */}
        {activeView === 'editor' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Financial Data</h3>
            <EditableFinancialGrid
              data={data}
              onDataChange={(newData) => {
                setEditedFinancialData(newData.financial_data);
                if (onDataUpdate) onDataUpdate(newData);
              }}
              onRecalculate={() => {
                // Trigger recalculation
                setLoading(true);
              }}
            />
          </div>
        )}

        {/* K-1 Summary View */}
        {activeView === 'k1summary' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">K-1 Partner Summary</h3>
            <K1PartnerSummary tax1065Files={data.tax1065Files || []} />
          </div>
        )}
        
      </div>
    </WizardV2Layout>
  );
}
